import React, { useEffect, useRef, useState } from 'react';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import CopyAllOutlined from '@mui/icons-material/CopyAllOutlined';
import { Tooltip } from '@mui/material';
import styles from '../AskAISSE.module.scss';

const copyPromptToClipboard = async (promptText: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(promptText);
      return true;
    }
  } catch {
    // Fall through to execCommand fallback.
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = promptText;
    document.body.appendChild(textArea);
    textArea.select();
    const didCopy = document.execCommand('copy');
    document.body.removeChild(textArea);
    return didCopy;
  } catch {
    return false;
  }
};

export default function PromptBubble({ text }: { text: any }) {
  const promptText = typeof text === 'string' ? text : String(text ?? '');
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollHeight > 200);
    }
  }, [promptText]);

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  const handleCopyPrompt = async () => {
    const didCopy = await copyPromptToClipboard(promptText);
    if (!didCopy) return;

    setCopied(true);
    if (copyResetTimerRef.current) {
      clearTimeout(copyResetTimerRef.current);
    }
    copyResetTimerRef.current = setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <div className={styles.promptBubbleContainer}>
      <div className={styles.promptBubble}>
        <div
          ref={textRef}
          className={styles.messageBody}
          style={{
            maxHeight: !expanded && isOverflowing ? '150px' : 'none',
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            ...(!expanded && isOverflowing
              ? {
                  maskImage: 'linear-gradient(180deg, #000 60%, transparent)',
                  WebkitMaskImage: 'linear-gradient(180deg, #000 60%, transparent)',
                }
              : {}),
          }}
        >
          {promptText}
        </div>
        {isOverflowing && (
          <div className={styles.promptExpandAction}>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className={styles.promptToggleButton}
              aria-label={expanded ? 'Show less' : 'Show more'}
            >
              {expanded ? (
                <KeyboardArrowUp fontSize="small" />
              ) : (
                <KeyboardArrowDown fontSize="small" />
              )}
            </button>
          </div>
        )}
      </div>

      <div className={styles.promptCopyRow}>
        <Tooltip title={copied ? 'Copied' : 'Copy prompt'} placement="top" arrow>
          <button
            type="button"
            className={`${styles.copyButton} ${styles.promptCopyAction}`}
            aria-label="Copy prompt"
            onClick={handleCopyPrompt}
          >
            <CopyAllOutlined fontSize="small" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
